import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { take, timeout } from 'rxjs/operators';
import { DateTimeUtility } from 'src/app/appUtilities/dateTime.utility';
import { HttpConfigService } from 'src/app/core/httpConfig/httpConfigService';

@Injectable({
  providedIn: 'root'
})
export class KdsService {

  api = this.apiConfig.getBaseUrl;

  activeOrder = new BehaviorSubject(null);
  private readonly orderRequestTimeoutMs = 8000;
  /** Keep the backend fetch window in lockstep with KdsResolver.filterOrdersByStoreDate. */
  private readonly orderDateLeewayMs = 60 * 60 * 1000;
  private readonly basicInfoStorageKey = 'basic-info';

  constructor(
    private apiConfig: HttpConfigService,
    private _dateTimeUtility: DateTimeUtility
    ) {
  }

  /**
   * get all routes
   * @returns 
   */
  getAllRoutes(): Observable<any> {
    return this.apiConfig.get(this.api + 'kds-route-config');
  }

  /**
   * get order status list
   * @returns 
   */
  getOrderStatusList(): Observable<any> {
    return this.apiConfig.get(this.api + 'orderStatus/getAll');
  }

  

  getOrdersByRouteAndStatus(routeName, status, storeId): Observable<any> {
    const { from, to } = this.getOrderWindow();
    return this.withOrderTimeout(
      this.apiConfig.get(this.api + 'order/' + from + '/' + to + '/' + encodeURIComponent(routeName) + '/' + encodeURIComponent(status) + '?storeId=' + storeId)
    );
  }

  getKdsFeed(routeId: number, storeId: number, statuses: string[] = ['Pending', 'Accepted', 'Prepared']): Observable<any> {
    const params = [
      'storeId=' + encodeURIComponent(String(storeId)),
      'routeId=' + encodeURIComponent(String(routeId)),
      'states=PROVISIONAL,COMMITTED',
      'statuses=' + encodeURIComponent(statuses.join(',')),
    ].join('&');
    return this.withOrderTimeout(
      this.apiConfig.get(this.api + 'kds/orders?' + params)
    );
  }

  commitKdsOrder(orderId: number | string): Observable<any> {
    return this.apiConfig.post(this.api + 'kds/orders/' + orderId + '/commit', {});
  }

  updateKdsLineState(orderId: number | string, orderDetailId: number | string, done: boolean): Observable<any> {
    return this.apiConfig.post(this.api + 'kds/orders/' + orderId + '/line-state', {
      orderDetailId,
      done,
    });
  }

  updateKdsStatus(orderId: number | string, statusId: number | string): Observable<any> {
    return this.apiConfig.patch(this.api + 'kds/orders/' + orderId + '/status', { statusId });
  }

  deleteKdsOrder(orderId: number | string): Observable<any> {
    return this.apiConfig.delete(this.api + 'kds/orders/' + orderId);
  }

  getOrdersByRoute(routeName, storeId): Observable<any> {
    const { from, to } = this.getOrderWindow();
    return this.withOrderTimeout(
      this.apiConfig.get(this.api + 'order/' + from + '/' + to + '/' + encodeURIComponent(routeName) + '?storeId=' + storeId)
    );
  }

  getOrdersByDateRange(storeId): Observable<any> {
    const { from, to } = this.getOrderWindow();
    return this.withOrderTimeout(
      this.apiConfig.get(this.api + 'order/order-range?from=' + from + '&to=' + to + '&storeId=' + storeId)
    );
  }

  getCashierOrdersByDateRange(storeId): Observable<any> {
    const { from, to } = this.getOrderWindow();
    return this.withOrderTimeout(
      this.apiConfig.get(this.api + 'order/cashier/order-range?from=' + from + '&to=' + to + '&storeId=' + storeId)
    );
  }

  private getOrderWindow(): { from: number; to: number } {
    // Use the store-timezone day window with the same leeway the resolver applies when filtering
    // results client-side. The old device-local midnight window (no leeway) could fetch the wrong
    // day on a device in another timezone, and dropped just-before-midnight tickets the client
    // leeway was meant to keep visible (the backend had already excluded them from the range).
    return this._dateTimeUtility.getCurrentStoreDateWindow(
      this.getStoreTimeZone(),
      this.orderDateLeewayMs,
    );
  }

  private getStoreTimeZone(): string {
    try {
      const basicInfo = JSON.parse(localStorage.getItem(this.basicInfoStorageKey) || 'null');
      return basicInfo?.timezone || '';
    } catch {
      return '';
    }
  }

  private withOrderTimeout(request$: Observable<any>): Observable<any> {
    return request$.pipe(
      timeout(this.orderRequestTimeoutMs),
      take(1)
    );
  }


  
  
}
